import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import type { TaskConfigSettings } from '@pms/shared';
import { tasksService } from '@/services/tasks.service';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useConfigSettings } from './useConfigSettings';
import { ConfigTabShell } from './ConfigTabShell';

function Form({ initial, save, saving }: { initial: TaskConfigSettings; save: (p: Partial<TaskConfigSettings>) => void; saving: boolean }) {
  const [enabled, setEnabled] = useState(initial.reminder_global_enabled);
  const [lead, setLead] = useState(String(initial.reminder_lead_minutes));
  const dirty = enabled !== initial.reminder_global_enabled || Number(lead) !== initial.reminder_lead_minutes;

  const runNow = useMutation({
    mutationFn: () => tasksService.runReminders(),
    onSuccess: (r) => (r.skipped
      ? toast.info('Reminders are switched off — nothing sent')
      : toast.success(`Sent ${r.notifications_sent} reminder(s) across ${r.occurrences} task(s)`)),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card className="max-w-lg" variant="flat">
      <CardHeader><CardTitle>Reminder Rules</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label>Enable reminders</Label>
            <p className="text-xs text-muted-foreground">Master switch for task due reminders.</p>
          </div>
          <Switch checked={enabled} onChange={setEnabled} aria-label="Enable reminders" />
        </div>
        <div className="space-y-1.5">
          <Label>Lead time (minutes before due)</Label>
          <Input type="number" min={0} max={1440} value={lead} onChange={(e) => setLead(e.target.value)} disabled={!enabled} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button disabled={!dirty || saving} onClick={() => save({ reminder_global_enabled: enabled, reminder_lead_minutes: Number(lead) })}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outline" disabled={runNow.isPending} onClick={() => runNow.mutate()}>
            <Send className="h-4 w-4" /> {runNow.isPending ? 'Sending…' : 'Send due reminders now'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Reminders are swept every minute and fire once per occurrence. Tasks must also have
          Enable Reminder switched on individually.
        </p>
      </CardContent>
    </Card>
  );
}

export function ReminderRulesTab() {
  const { data, isLoading, isError, refetch, save, saving } = useConfigSettings();
  return (
    <ConfigTabShell isLoading={isLoading} isError={isError} onRetry={refetch}>
      {data && <Form initial={data} save={save} saving={saving} />}
    </ConfigTabShell>
  );
}
